import type { Map as MapLibreMap, MapGeoJSONFeature, PointLike } from 'maplibre-gl';

/** Layers whose features take clicks (e.g. station circles), so the map behind them does not. */
export interface InteractiveLayers {
  register: (ids: readonly string[]) => () => void;
  /** While claimed (e.g. a popup is open), a click on the map only dismisses; returns the release. */
  claimMapClicks: () => () => void;
  mapClicksClaimed: () => boolean;
  /** Topmost feature of a registered layer at a screen point, if any. */
  featureAt: (
    map: MapLibreMap,
    point: { x: number; y: number },
    ids?: readonly string[],
  ) => MapGeoJSONFeature | undefined;
}

/** Slack around the pointer, px, so small symbols are easy to hit. */
const TOLERANCE = 4;

export function createInteractiveLayers(): InteractiveLayers {
  const counts = new Map<string, number>();
  let claims = 0;
  return {
    claimMapClicks() {
      claims++;
      return () => void claims--;
    },
    mapClicksClaimed: () => claims > 0,
    register(ids) {
      for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
      return () => {
        for (const id of ids) {
          const n = (counts.get(id) ?? 1) - 1;
          if (n > 0) counts.set(id, n);
          else counts.delete(id);
        }
      };
    },
    featureAt(map, { x, y }, ids = [...counts.keys()]) {
      const layers = ids.filter((id) => map.getLayer(id));
      if (!layers.length) return undefined;
      const box: [PointLike, PointLike] = [
        [x - TOLERANCE, y - TOLERANCE],
        [x + TOLERANCE, y + TOLERANCE],
      ];
      return map.queryRenderedFeatures(box, { layers })[0];
    },
  };
}
